// src/pages/BuddyBot.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {auth, db, storage } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, addDoc, deleteDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from "framer-motion";
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { useRef } from 'react';
import { ref as storageRef, deleteObject, uploadBytes, getDownloadURL } from "firebase/storage";


const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const teacherApiUrl = "https://teacherapi-jrox57cr4q-uc.a.run.app";

const BuddyBot = () => {
  const [questionModalBook, setQuestionModalBook] = useState(null); // book for which modal is open
const [questionInput, setQuestionInput] = useState("");

  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const { t, i18n } = useTranslation();
  const [books, setBooks] = useState([]);
const [loadingBooks, setLoadingBooks] = useState(true);
const fileInputRef = useRef(null);
const [showBooks, setShowBooks] = useState(true);
const [showUpgradeModal, setShowUpgradeModal] = useState(false);


  const [role, setRole] = useState('student');
  const [teacherStudents, setTeacherStudents] = useState([]);
  const [selectedStudentIndex, setSelectedStudentIndex] = useState(0);
  const [newStudent, setNewStudent] = useState({ name: '', grade: '', strengths: '', areasForGrowth: '' });
  const [teacherTools, setTeacherTools] = useState([
    { label: "Generate Quiz", endpoint: "/generate-quiz", payload: { topic: "Photosynthesis", grade: 6 } },
    { label: "Lesson Plan", endpoint: "/lesson-plan", payload: { topic: "Algebra", grade: 7 } },
    { label: "Worksheet", endpoint: "/worksheet", payload: { topic: "Adjectives", grade: 5 } },
    { label: "Seating Plan", endpoint: "/seating-plan", payload: { studentCount: 30, groups: 5 } },
    { label: "Report Comment", endpoint: "/report-comment", payload: {} }
  ]);
  const [showJournal, setShowJournal] = useState(false);
  const [streak, setStreak] = useState(0);
    const [accessType, setAccessType] = useState('demo');



  const [journal, setJournal] = useState(() => {
  const saved = localStorage.getItem('buddyJournal');
  return saved ? JSON.parse(saved) : [];
});

const [searchQuery, setSearchQuery] = useState("");
const [currentPage, setCurrentPage] = useState(1);
const itemsPerPage = 5;

const filteredJournal = journal.filter(j =>
  j.entry.toLowerCase().includes(searchQuery.toLowerCase()) ||
  j.tag.toLowerCase().includes(searchQuery.toLowerCase())
);

const totalPages = Math.ceil(filteredJournal.length / itemsPerPage);
const currentEntries = filteredJournal.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  
// Fetch journal entries from Firestore on load (if logged in)
useEffect(() => {
  const fetchJournal = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    try {
      const ref = collection(db, `users/${currentUser.uid}/journalEntries`);
      const snapshot = await getDocs(ref);
      const entries = snapshot.docs.map(doc => doc.data());
      setJournal(entries);
    } catch (err) {
      console.error("Failed to fetch journal entries:", err);
    }
  };
  fetchJournal();
}, []);


// Function to add to journal + Firestore
const addToJournal = async (entry) => {
  const existingTags = [...new Set(journal.map(j => j.tag))];

  const modal = document.createElement("div");
  modal.style.position = "fixed";
  modal.style.top = "0";
  modal.style.left = "0";
  modal.style.width = "100%";
  modal.style.height = "100%";
  modal.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
  modal.style.display = "flex";
  modal.style.alignItems = "center";
  modal.style.justifyContent = "center";
  modal.style.zIndex = 9999;

  const dialog = document.createElement("div");
  dialog.style.backgroundColor = "white";
  dialog.style.padding = "20px";
  dialog.style.borderRadius = "10px";
  dialog.style.width = "300px";
  dialog.style.textAlign = "center";

  const label = document.createElement("p");
  label.innerText = "📘 Choose a tag or enter your own:";
  label.className = "text-sm font-medium mb-2";

  const tagDropdown = document.createElement("select");
  tagDropdown.className = "w-full p-2 rounded border mb-2";
  existingTags.forEach(t => {
    const opt = document.createElement("option");
    opt.value = t;
    opt.textContent = t;
    tagDropdown.appendChild(opt);
  });

  const customInput = document.createElement("input");
  customInput.type = "text";
  customInput.placeholder = "Or enter a new tag...";
  customInput.className = "w-full p-2 rounded border mb-2";

  const okButton = document.createElement("button");
  okButton.innerText = "OK";
  okButton.className = "mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600";

  dialog.appendChild(label);
  dialog.appendChild(tagDropdown);
  dialog.appendChild(customInput);
  dialog.appendChild(okButton);
  modal.appendChild(dialog);
  document.body.appendChild(modal);

  const tag = await new Promise(resolve => {
    okButton.onclick = () => {
      const value = customInput.value.trim() || tagDropdown.value || "General";
      resolve(value);
      document.body.removeChild(modal);
    };
  });

  const taggedEntry = { entry, tag };
  const updated = [...journal, taggedEntry];
  setJournal(updated);
  localStorage.setItem('buddyJournal', JSON.stringify(updated));
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const ref = collection(db, `users/${currentUser.uid}/journalEntries`);
      await addDoc(ref, taggedEntry);
    }
  } catch (err) {
    console.error("Failed to save journal entry:", err);
  }
};
  // Function to run teacher tools
  const runTeacherTool = async (tool) => {
    if (!tool || loading) return;
    const payload = { ...tool.payload };

    if (tool.label === "Report Comment" && teacherStudents.length > 0) {
      const student = teacherStudents[selectedStudentIndex];
      payload.studentName = student.name;
      payload.grade = student.grade;
      payload.strengths = student.strengths;
      payload.areasForGrowth = student.areasForGrowth;
    }

    setLoading(true);
    setHistory(prev => [...prev, { role: 'user', content: `Run: ${tool.label}` }, { role: 'assistant', content: t("BuddyThinking") + " ..." }]);
    try {
      const res = await fetch(`${teacherApiUrl}${tool.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      const content = Object.values(data)[0];
const botMessage = {
  role: 'assistant',
  content: data.answer || "Sorry, I don't have an answer for that right now.",
  image: data.image || null
};
      setHistory(prev => [...prev.slice(0, -1), botMessage]);
    } catch (err) {
      setHistory(prev => [...prev.slice(0, -1), { role: 'assistant', content: t("ErrorMessage") }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      if (user && role === 'teacher') {
        const snapshot = await getDocs(collection(db, `teachers/${user.uid}/students`));
        const students = snapshot.docs.map(doc => doc.data());
        setTeacherStudents(students);
      }
    };
    fetchStudents();
  }, [user, role]);

  // Removed StudentSelector block – replaced with inline report button

  const handleAddStudent = async () => {
    if (!user || !newStudent.name || !newStudent.grade) return;
    try {
      const ref = collection(db, `teachers/${user.uid}/students`);
      await addDoc(ref, newStudent);
      setTeacherStudents(prev => [...prev, newStudent]);
      setNewStudent({ name: '', grade: '', strengths: '', areasForGrowth: '' });
    } catch (err) {
      console.error("Error adding student:", err);
    }
  };

  const handleDeleteStudent = async (index) => {
    if (!user) return;
    const studentToDelete = teacherStudents[index];
    const snapshot = await getDocs(collection(db, `teachers/${user.uid}/students`));
    const targetDoc = snapshot.docs.find(doc => {
      const data = doc.data();
      return data.name === studentToDelete.name && data.grade === studentToDelete.grade;
    });
    if (targetDoc) {
      const ref = targetDoc.ref;
      await deleteDoc(ref);
      setTeacherStudents(prev => prev.filter((_, i) => i !== index));
    if (selectedStudentIndex === index) setSelectedStudentIndex(0);
    }
  };

  // Student List with Report Button
  const renderTeacherStudents = () => (
    <div className="mt-4">
      <h3 className="text-md font-semibold text-purple-800 mb-2">Your Students:</h3>
      {teacherStudents.map((student, index) => (
        <div key={index} className="border p-3 rounded-lg mb-2 bg-white shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium">{student.name} (Grade {student.grade})</p>
              <p className="text-xs text-gray-500">Strengths: {student.strengths || 'N/A'}</p>
              <p className="text-xs text-gray-500">Growth Areas: {student.areasForGrowth || 'N/A'}</p>
            </div>
            <div className="flex gap-2">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                onClick={() => runTeacherTool({
                  label: "Report Comment",
                  endpoint: "/report-comment",
                  payload: {
                    studentName: student.name,
                    grade: student.grade,
                    strengths: student.strengths,
                    areasForGrowth: student.areasForGrowth
                  }
                })}
              >
                Report
              </button>
              <button
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                onClick={() => handleDeleteStudent(index)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
  // Teacher Dashboard with Add Student
  const TeacherDashboard = useMemo(() => (
  role === 'teacher' && (
    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4 shadow">
      <h2 className="text-lg font-bold text-purple-800 mb-3">🎓 Manage Students</h2>
            <div className="mb-4">
        <input
          className="w-full border p-2 rounded mb-2"
          placeholder="Student Name"
          value={newStudent.name}
          onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
        />
        <input
          className="w-full border p-2 rounded mb-2"
          placeholder="Grade"
          type="number"
          value={newStudent.grade}
          onChange={(e) => setNewStudent({ ...newStudent, grade: e.target.value })}
        />
        <input
          className="w-full border p-2 rounded mb-2"
          placeholder="Strengths"
          value={newStudent.strengths}
          onChange={(e) => setNewStudent({ ...newStudent, strengths: e.target.value })}
        />
        <input
          className="w-full border p-2 rounded mb-2"
          placeholder="Areas for Growth"
          value={newStudent.areasForGrowth}
          onChange={(e) => setNewStudent({ ...newStudent, areasForGrowth: e.target.value })}
        />
        <button
          onClick={handleAddStudent}
          className="px-4 py-2 bg-purple-600 text-white rounded shadow hover:bg-purple-700"
        >
          ➕ Add Student
        </button>
      </div>
    </div>
  )
), [role, newStudent, teacherStudents]);


    // ✨ Teacher Tools UI Panel ✨
  const TeacherToolPanel = () => (
    role === 'teacher' && (
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4 shadow">
        <h2 className="text-lg font-bold text-purple-800 mb-2">🛠️ Teacher's Tools</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {teacherTools.map((tool, index) => (
            <button
              key={index}
              onClick={() => runTeacherTool(tool)}
              className="px-4 py-2 bg-yellow-100 hover:bg-yellow-200 text-purple-900 font-semibold rounded shadow text-sm"
            >
              {tool.label}
            </button>
          ))}
        </div>
      </div>
    )
  );

  // Chat history and UI state
  const [history, setHistory] = useState([
    { role: 'assistant', content: t("Welcome") + " " + t("TypeMessage") }
  ]);


  const [imageLoaded, setImageLoaded] = useState({});
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

    // Mic/voice state
  const [listening, setListening] = useState(false);
  const [micLang, setMicLang] = useState('ta-IN');
  const [lastInputFromMic, setLastInputFromMic] = useState(false);

  // User role and memory
  const [showMemory, setShowMemory] = useState(false);
  const [memoryItems, setMemoryItems] = useState(() => {
    const stored = localStorage.getItem('buddyMemory');
    return stored ? JSON.parse(stored) : [];
  });

  // Daily send-limit (demo users)
  const [sendCount, setSendCount] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [loadingLimit, setLoadingLimit] = useState(true);
  const maxSends = 10;

 // Fetch user role
  useEffect(() => {
    const fetchLimits = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const ref = doc(db, 'users', user.uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;

      const data = snap.data();
      if (data.role) setRole(data.role.toLowerCase());
      if (data.accessType) setAccessType(data.accessType);
    };
    fetchLimits();
    const fetchBooks = async () => {
  const user = auth.currentUser;
  if (!user) return;
  const booksRef = collection(db, 'users', user.uid, 'books');
  const snap = await getDocs(booksRef);
  const fetchedBooks = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  setBooks(fetchedBooks);
  setLoadingBooks(false);
};
fetchBooks();

  }, []);


  useEffect(() => {
  const fetchStreak = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'users', user.uid);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      let newStreak = 1;
      if (data.lastActiveDate === today) {
        newStreak = data.streak || 1;
      } else if (data.lastActiveDate === yesterday) {
        newStreak = (data.streak || 0) + 1;
      }
      await updateDoc(ref, { streak: newStreak, lastActiveDate: today });
      setStreak(newStreak);
    }
  };
  fetchStreak();
}, []);

  // Fetch & reset daily send count using accessType for demo/pro
useEffect(() => {
  const fetchLimits = async () => {
    const user = auth.currentUser;
    if (!user) { setLoadingLimit(false); return; }

    const ref = doc(db, 'users', user.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) { setLoadingLimit(false); return; }
    const data = snap.data();

    const accessType = data.accessType || 'demo';
    const demo = accessType !== 'pro';
    setIsDemo(demo);

    const today = new Date().toDateString();
    let count = data.buddySendCount || 0;
    if (data.buddySendDate !== today) {
      await updateDoc(ref, { buddySendCount: 0, buddySendDate: today });
      count = 0;
    }
    setSendCount(count);
    if (demo && count >= maxSends) setLimitReached(true);

    setLoadingLimit(false);
  };
  fetchLimits();
}, [loadingLimit]); // ADD loadingLimit or relevant state if used


  // Fetch chat history
useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (!user) return;
    const chatRef = doc(db, 'chat_history', user.uid);
    const snap = await getDoc(chatRef);
    if (snap.exists() && Array.isArray(snap.data().messages)) {
      setHistory(snap.data().messages);
    }
  });
  return unsubscribe;
}, []);


// Core sendMessage with demo-limit enforcement
  const sendMessage = async (messageContent = input) => {
    if (loading || loadingLimit) return;
    if (isDemo && limitReached) {
      window.location.href = '/upgrade';
      return;
    }
    if (!messageContent.trim()) return;
      setInput('');   

    // Append user and thinking
    const userMessage = { role: 'user', content: messageContent.trim() };
    const thinking = { role: 'assistant', content: t("BuddyThinking") + " ..." };
    setHistory(prev => [...prev, userMessage, thinking]);

    // Update memory
    setMemoryItems(prev => {
      const updated = [...prev, `You asked: ${messageContent.trim()}`];
      localStorage.setItem('buddyMemory', JSON.stringify(updated));
      return updated;
    });

    setLoading(true);
    const user = auth.currentUser;
    const payload = {
      messages: [...history, userMessage],
      age: 10,
      lang: messageContent.match(/[஀-௿]/) ? 'ta' : 'en',
      tone: 'colloquial-tanglish',
      role,
      userId: user?.uid
    };

    try {
      // Call backend
      const res = await fetch('https://api-jrox57cr4q-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      const botMessage = {
        role: 'assistant',
        content: data.answer || "Sorry, I don't have an answer for that right now.",
        image: data.image || null
      };



   // Update history
      setHistory(prev => {
        const withoutThinking = prev.slice(0, -1);
        return [...withoutThinking, botMessage];
      });

      // Update send-count in Firestore for demo
      if (user && isDemo) {
        const ref = doc(db, 'users', user.uid);
        const today = new Date().toDateString();
        const newCount = sendCount + 1;
        await updateDoc(ref, { buddySendCount: newCount, buddySendDate: today });
        setSendCount(newCount);
        if (newCount >= maxSends) {
          setLimitReached(true);
          window.location.href = '/upgrade';
          return;
        }
      }

      if (lastInputFromMic) {
        let replyText = botMessage.content;
        let lang = input.match(/[஀-௿]/) ? 'ta-IN' : 'en-US';
        if (lang === 'ta-IN') {
          if (role === 'student') replyText = `${botMessage.content}`;
          else if (role === 'teacher') replyText = `${botMessage.content}`;
          else if (role === 'parent') replyText = `${botMessage.content}`;
        }
const replyUtterance = new SpeechSynthesisUtterance(replyText);
replyUtterance.lang = lang;
replyUtterance.pitch = 1;
replyUtterance.rate = 1;
const voices = window.speechSynthesis.getVoices();
if (voices.length) {
  replyUtterance.voice = voices.find(v => v.lang === lang) || voices.find(v => v.name.toLowerCase().includes('google'));
}
window.speechSynthesis.speak(replyUtterance);
      }
      if (user) {
        const ref = doc(db, 'chat_history', user.uid);
        await import('firebase/firestore').then(({ setDoc }) =>
          setDoc(ref, { messages: history }) // This can remain as-is or you can also use latest local state.
        );
      }
    } catch (err) {
      const fallback = { role: 'assistant', content: t("ErrorMessage") };
      setHistory(prev => [...prev.slice(0, -1), fallback]);
      const user = auth.currentUser;
    } finally {
      setLoading(false);
    }
  };

  const handleMicInput = () => {
    const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
    recognition.lang = micLang;
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setLastInputFromMic(true);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setListening(false);
    };

    recognition.start();
  };

  const scrollToInput = () => {
    const inputBox = document.getElementById('chatInputBox');
    if (inputBox) {
      inputBox.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputBox.focus(), 500);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const scrollBtn = document.getElementById('scrollToInputBtn');
        if (scrollBtn) scrollBtn.style.display = entry.isIntersecting ? 'none' : 'block';
      },
      { threshold: 0.8 }
    );
    const inputBox = document.getElementById('chatInputBox');
    if (inputBox) observer.observe(inputBox);
    return () => observer.disconnect();
  }, []);

    // Quick-message helper (apply same limit logic)
  const sendQuickMessage = async (question) => {
    if (loading || loadingLimit) return;
    if (isDemo && limitReached) {
      window.location.href = '/upgrade';
      return;
    }
    const userMessage = { role: 'user', content: question };
    const thinkingMessage = { role: 'assistant', content: t("BuddyThinking") + " ..." };
    setHistory(prev => [...prev, userMessage, thinkingMessage]);
    setLoading(true);
    const user = auth.currentUser;

    try {
      const res = await fetch('https://api-jrox57cr4q-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [...history, userMessage],
          age: 10,
          lang: question.match(/[஀-௿]/) ? 'ta' : 'en',
          tone: 'colloquial-tanglish',
          role,
          userId: user?.uid
        }),
      });
      const data = await res.json();
      const botMessage = { role: 'assistant', content: data.answer || t("NoAnswer"), image: data.image || null };

      setHistory(prev => {
        const withoutThinking = prev.slice(0, -1);
        return [...withoutThinking, botMessage];
      });

      if (user && isDemo) {
        const ref = doc(db, 'users', user.uid);
        const today = new Date().toDateString();
        const newCount = sendCount + 1;
        await updateDoc(ref, { buddySendCount: newCount, buddySendDate: today });
        setSendCount(newCount);
        if (newCount >= maxSends) {
          setLimitReached(true);
          window.location.href = '/upgrade';
          return;
        }
      }

      if (user) {
        const ref = doc(db, 'chat_history', user.uid);
        await import('firebase/firestore').then(({ setDoc }) =>
          setDoc(ref, { messages: [...history, botMessage] })
        );
      }
    } catch (err) {
      setHistory(prev => [...prev.slice(0, -1), { role: 'assistant', content: t("ErrorMessage") }]);
      const user = auth.currentUser;
      if (user) {
        const ref = doc(db, 'chat_history', user.uid);
        await import('firebase/firestore').then(({ setDoc }) =>
          setDoc(ref, { messages: history })
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const chatArea = document.querySelector('.flex-1.overflow-y-auto');
    if (chatArea) {
      setTimeout(() => {
        chatArea.scrollTo({ top: chatArea.scrollHeight, behavior: 'smooth' });
      }, 50);
    }
  }, [history]);

  useEffect(() => {
    const box = document.getElementById('chatInputBox');
    if (box) box.focus();
  }, []);
    useEffect(() => {
    const storeLearningPlan = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      try {
const ref = doc(db, `users/${currentUser.uid}/learningPlans/default`);
        await setDoc(ref, {
          fundamentals: true,
          interestBased: true,
          practiceQuests: true,
          aheadGoals: true,
          updated: new Date().toISOString(),
        });
      } catch (err) {
        console.error("Failed to sync learning plan:", err);
      }
    };
    storeLearningPlan();
  }, []);


const handleUpload = async () => {
    console.log("📥 Upload triggered");

    const file = fileInputRef.current?.files?.[0];
    if (!file) return alert("Please select a file.");

    const currentUser = auth.currentUser;
    if (!currentUser) return alert("You must be logged in.");

    const token = uuidv4();
    const bookName = file.name;

  
      const bookRef = storageRef(storage, `books/${currentUser.uid}/${bookName}`);
      await uploadBytes(bookRef, file);
      console.log("✅ File upload success");
          const downloadURL = await getDownloadURL(bookRef);
    alert("Upload successful");
      const metaRef = collection(db, "users", currentUser.uid, "books");
      await addDoc(metaRef, {
        name: bookName,
        uploadedAt: new Date().toISOString(),
        url: downloadURL,
      });
      console.log("✅ Firestore write success");

      fileInputRef.current.value = '';
    await fetch('https://api-jrox57cr4q-uc.a.run.app/process-pdf', {
  method: 'POST',
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    uid: currentUser.uid,
    filename: bookName,
    url: downloadURL
  })
});


     alert("Book uploaded and sent for processing.Buddy can now read it!");
    
  };  

  const handleDeleteBook = async (book) => {
  if (!window.confirm(`Delete "${book.name}"? This cannot be undone.`)) return;

  const user = auth.currentUser;
  if (!user) return alert("You must be logged in.");

  try {
    // 1. Remove from Firestore
    const bookDocRef = doc(db, "users", user.uid, "books", book.id);
    await deleteDoc(bookDocRef);

    // 2. Remove from Storage
    const path = `books/${user.uid}/${book.name}`; // book.name is filename
    const fileRef = storageRef(storage, path);
    await deleteObject(fileRef);

    // 3. Update local UI
    setBooks(books => books.filter(b => b.id !== book.id));
    alert(`"${book.name}" deleted!`);
  } catch (err) {
    console.error("Failed to delete book:", err);
    alert("Failed to delete. Please try again.");
  }
};


const handleDeleteAllBooks = async () => {
  if (!window.confirm("Delete ALL uploaded books? This cannot be undone.")) return;

  const user = auth.currentUser;
  if (!user) return alert("You must be logged in.");

  try {
    for (const book of books) {
      // 1. Delete from Firestore
      const bookDocRef = doc(db, "users", user.uid, "books", book.id);
      await deleteDoc(bookDocRef);

      // 2. Delete from Storage
      const path = `books/${user.uid}/${book.name}`;
      const fileRef = storageRef(storage, path);
      await deleteObject(fileRef);
    }
    setBooks([]); // Clear UI
    alert("All books deleted!");
  } catch (err) {
    console.error("Failed to delete all books:", err);
    alert("Failed to delete all books. Please try again.");
  }
};

// Summarize whole book
const handleReadBook = async (book) => {
  navigate(`/book-summary/${encodeURIComponent(book.name)}`);
  const user = auth.currentUser;
  if (!user) return alert("You must be logged in.");
  setLoading(true);

  try {
    const response = await fetch('https://api-jrox57cr4q-uc.a.run.app/read-book', {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        uid: user.uid,
        bookname: book.name, // If you use book.id in backend, use book.id here
        action: "summarize"
      }),
    });
    const data = await response.json();
    setHistory(prev => [
      ...prev,
      { role: "assistant", content: data.answer || "No summary returned." }
    ]);
  } catch (err) {
    alert("Buddy could not read the book.");
  }
  setLoading(false);
};

const handleReadBookWithQuestion = (book) => {
  const user = auth.currentUser;
  if (!user) return alert("You must be logged in.");
  
   setQuestionModalBook(book);
  setQuestionInput(""); // clear previous
};


// Generate a quiz from the book
const handleReadBookQuiz = async (book) => {
    navigate(`/book-quiz/${encodeURIComponent(book.name)}`);
  const user = auth.currentUser;
  if (!user) return alert("You must be logged in.");
  setLoading(true);

  try {
    const response = await fetch('https://api-jrox57cr4q-uc.a.run.app/read-book', {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        uid: user.uid,
        bookname: book.name,
        action: "quiz"
      }),
    });
    const data = await response.json();
    setHistory(prev => [
      ...prev,
      { role: "assistant", content: data.answer || "No quiz returned." }
    ]);
  } catch (err) {
    alert("Buddy could not quiz you on the book.");
  }
  setLoading(false);
};

{showUpgradeModal && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
    <div className="bg-white p-8 rounded-xl shadow-xl text-center max-w-xs">
      <h2 className="text-xl font-bold mb-3 text-blue-800">Pro Feature</h2>
      <p className="mb-4 text-gray-700">Upgrade to Pro to unlock unlimited book uploads, smart summaries, quizzes, and more!</p>
      <a
        href="/upgrade"
        className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full font-bold hover:bg-blue-700"
      >
        Upgrade Now
      </a>
      <button
        className="block mt-4 text-gray-500 hover:underline"
        onClick={() => setShowUpgradeModal(false)}
      >
        Maybe later
      </button>
    </div>
  </div>
)}



  // Render the main BuddyBot component

  return (
    <div className="flex flex-col min-h-screen p-2 sm:p-4 md:p-6 w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto bg-white shadow rounded-xl">
      <h1 className="text-3xl font-bold mb-2 text-center text-purple-700">{t("Welcome")}</h1>
      <TeacherToolPanel />
      {TeacherDashboard}

            {role === 'student' && (

      
  <div className="bg-green-50 border border-green-200 p-4 rounded-xl mb-4">
    <h2 className="text-lg font-bold text-green-800 mb-2">📘 My Learning Plan</h2>
    <ul className="list-disc list-inside text-sm text-gray-800">
      <li>📖 Fundamentals: Let’s master core skills in Math and English.</li>
      <li>🎨 Interest-based fun: Drawing, puzzles, or vocabulary if they like stories!</li>
      <li>🧠 Practice Quests: Daily word games and memory challenges.</li>
      <li>🚀 Get Ahead: Learn next-grade topics early with Buddy’s help.</li>
    </ul>
    <p className="text-xs text-gray-500 mt-2">(Buddy personalizes this based on your age and interests. More coming soon!)</p>
  </div>
)}

      <div className="flex gap-2 justify-end mb-2">
        <button
          onClick={() => i18n.changeLanguage('en')}
          className={`px-2 py-1 rounded ${i18n.language === 'en' ? 'bg-purple-200 text-purple-900' : 'bg-gray-200 text-gray-500'}`}
          aria-label={t("MicEnglish")}
        >
          English
        </button>
        <button
          onClick={() => i18n.changeLanguage('ta')}
          className={`px-2 py-1 rounded ${i18n.language === 'ta' ? 'bg-purple-200 text-purple-900' : 'bg-gray-200 text-gray-500'}`}
          aria-label={t("MicTamil")}
        >
          தமிழ்
        </button>
      </div>

      {showMemory && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4 text-sm text-gray-700 shadow-inner">
          <h2 className="font-semibold text-purple-700 mb-2">{t("WhatIRemember")}</h2>
          <ul className="list-disc pl-5 space-y-1">
            {memoryItems.map((item, idx) => (
              <li key={idx} className="flex justify-between items-center">
                <span>{item}</span>
                <button
                  onClick={() => setMemoryItems(prev => {
                    const updated = prev.filter((_, i) => i !== idx);
                    localStorage.setItem('buddyMemory', JSON.stringify(updated));
                    return updated;
                  })}
                  className="text-xs text-red-500 ml-3 hover:underline"
                  aria-label={t("Forget")}
                >{t("Forget")}</button>
              </li>
            ))}
          </ul>
        </div>
      )}
             {/* Limit warning */}
      {isDemo && limitReached && !loadingLimit && (
        <div className="text-center text-red-600 font-bold mb-4">
          {t("YouveReachedLimit")} <a href="/upgrade" className="underline">{t("UpgradeForUnlimited")}</a>
        </div>
      )}

      
      <div className="flex flex-wrap gap-2 mb-4 justify-center">

{/* ✨ Student Buddy Action Buttons */}
{role === 'student' && (
  <div className="flex flex-wrap gap-3 justify-center mb-4">
    <button
      onClick={() => setHistory(prev => [...prev, {
        role: 'assistant',
        content: '🎯 Ready for a mini quest? Try answering this: What are synonyms of happy?'
      }])}
      className="px-3 py-1 text-sm bg-yellow-100 hover:bg-yellow-200 text-purple-800 rounded-full shadow"
    >
      🎯 Mini Quest
    </button>

    <button
      onClick={() => {
        const last = [...history].reverse().find(msg => msg.role === 'assistant');
        if (last) addToJournal(last.content);
      }}
      className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-purple-800 rounded-full shadow"
    >
      📘 Save Last Answer
    </button>
  </div>
)}


        {(role === 'student' ? [
          t("ExampleStudentQ1"),
          t("ExampleStudentQ2"),
          t("ExampleStudentQ3")
        ] : role === 'teacher' ? [
          t("ExampleTeacherQ1"),
          t("ExampleTeacherQ2"),
          t("ExampleTeacherQ3")
        ] : [
          t("ExampleParentQ1"),
          t("ExampleParentQ2"),
          t("ExampleParentQ3")
        ]).map((question, index) => (
          <button
            key={index}
            onClick={() => sendQuickMessage(question)}
            className="px-3 py-1 text-sm bg-purple-100 hover:bg-purple-200 text-purple-800 rounded-full shadow transform transition-transform active:scale-90"
            aria-label={`${t("Ask")}: ${question}`}
          >
            {t("Ask")}: {question}
          </button>
        ))}
      </div>
      <p className="text-sm text-green-800 mb-2 font-medium">
  🔥 Daily Streak: <strong>{streak}</strong> days in a row!
</p>
<p className="text-sm text-green-800 font-medium mb-2">📊 Learning Stats:</p>
<ul className="list-disc list-inside text-sm text-gray-800 mb-2">
  <li>Total Entries: {journal.length}</li>
  <li>Tags Covered: {[...new Set(journal.map(j => j.tag))].length}</li>
  <li>Most Tagged: {(() => {
    const counts = journal.reduce((acc, j) => {
      acc[j.tag] = (acc[j.tag] || 0) + 1;
      return acc;
    }, {});
    const max = Math.max(...Object.values(counts));
    return Object.keys(counts).find(tag => counts[tag] === max);
  })()}</li>
</ul>

<div className="mt-2">
  <p className="text-sm text-green-800 font-medium mb-1">📈 Progress Tracker</p>
  <div className="grid grid-cols-2 gap-2">
    {(() => {
      const tagCounts = journal.reduce((acc, j) => {
        acc[j.tag] = (acc[j.tag] || 0) + 1;
        return acc;
      }, {});
      return Object.entries(tagCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([tag, count]) => {
          const percent = Math.min(100, count * 10);
          return (
            <div key={tag}>
              <div className="text-xs font-medium text-gray-600 mb-1">{tag} ({count})</div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${percent}%` }}></div>
              </div>
            </div>
          );
        });
    })()}
  </div>
</div>




      <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-[40vh] sm:max-h-[50vh]" role="log" aria-live="polite">
        <AnimatePresence initial={false}>
          {history.filter(m => m.role !== 'system').map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.3, delay: idx * 0.04 }}
              className={`p-3 rounded-xl shadow max-w-md ${msg.role === 'user'
                ? 'bg-blue-100 self-end text-right ml-auto'
                : 'bg-purple-100 self-start text-left mr-auto'}`}
            >
<span className="block whitespace-pre-line">{msg.content}</span>
{msg.image && (
  <div className="my-2 flex justify-center">
    <img
      src={msg.image}
      alt="Buddy's image"
      style={{ maxWidth: 320, maxHeight: 320, borderRadius: 12, boxShadow: '0 2px 16px #b6a6f8' }}
                onLoad={() => setImageLoaded(prev => ({ ...prev, [idx]: true }))}

    />
            {!imageLoaded[idx] && <span>Loading image...</span>}

  </div>
)}

{msg.role === 'assistant' && !msg.savedToJournal && (
  <button
    className="mt-2 text-xs text-purple-700 underline"
    onClick={() => addToJournal(msg.content)}
  >📘 Save to Journal</button>
)}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      <button
        onClick={() => setShowMemory(!showMemory)}
        className={`inline-flex items-center justify-center gap-2 px-3 py-1 mb-4 font-semibold text-sm rounded-full shadow transition-all duration-300 ${
          showMemory ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
        } w-full sm:w-auto`}
      >
        🧠 {showMemory ? t("HideMemory") : t("ViewMemory")}
      </button>
      {listening && (
        <div className="text-center text-purple-700 animate-pulse text-sm mb-4">
          👂 {t("BuddyListening")}
        </div>
      )}
      <div className="mb-2 flex gap-2 justify-end">
        <button
          onClick={() => setMicLang('en-US')}
          className={`px-2 py-1 rounded ${micLang === 'en-US' ? 'bg-purple-200 text-purple-900' : 'bg-gray-200 text-gray-500'}`}
          aria-label={t("MicEnglish")}
        >
          🎤 English
        </button>
        <button
          onClick={() => setMicLang('ta-IN')}
          className={`px-2 py-1 rounded ${micLang === 'ta-IN' ? 'bg-purple-200 text-purple-900' : 'bg-gray-200 text-gray-500'}`}
          aria-label={t("MicTamil")}
        >
          🎤 Tamil
        </button>
      </div>
      


      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <input
          id="chatInputBox"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter'&& sendMessage()}
          placeholder={t("Ask Buddy")}
          className="flex-1 p-3 border rounded shadow"
        />
        <button
          onClick={handleMicInput}
          disabled={loading}
          className={`bg-gradient-to-r from-yellow-100 to-purple-200 text-purple-900 font-bold py-2 px-4 rounded-full shadow-lg transition-all duration-300 ease-in-out hover:scale-105 hover:from-yellow-200 ${listening ? 'animate-wave ring-2 ring-purple-400 ring-offset-2 shadow-purple-500' : ''}`}
          aria-label={micLang === 'ta-IN' ? t("MicTamil") : t("MicEnglish")}
        >
          🎤
        </button>
        <button onClick={()=>sendMessage(input)} disabled={loading||loadingLimit|| (isDemo&&sendCount>=maxSends)}
          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700">{t("Send")}</button>
      </div>
      <div
        id="scrollToInputBtn"
        onClick={scrollToInput}
        className="fixed bottom-3 right-3 sm:bottom-6 sm:right-6 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg cursor-pointer animate-bounce z-50"
      >
        ⬇️ {t("ScrollToChat")}
      </div>
{role === 'student' && journal.length > 0 && showJournal && (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
    <h2 className="font-bold text-blue-700 mb-2">📘 My Learning Journal</h2>
    <input
      type="text"
      placeholder="Search journal..."
      className="w-full p-2 rounded border mb-2 text-sm"
      value={searchQuery}
      onChange={(e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
      }}
    />
    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
      {currentEntries.map((j, i) => (
        <li key={i} className="flex justify-between items-center">
          <div>
            <span className="font-semibold">[{j.tag}]</span> {j.entry}
          </div>
          <button
            onClick={async () => {
              const updated = journal.filter((_, idx) => idx !== i + (currentPage - 1) * itemsPerPage);
              setJournal(updated);
              localStorage.setItem('buddyJournal', JSON.stringify(updated));

              const user = auth.currentUser;
if (user) {
  const ref = collection(db, `users/${user.uid}/journalEntries`);
                const snapshot = await getDocs(ref);
                const toDelete = snapshot.docs.find(doc => doc.data().entry === j.entry && doc.data().tag === j.tag);
                if (toDelete) {
                  await deleteDoc(toDelete.ref);
                }
              }
            }}
            className="ml-2 text-xs text-red-500 hover:underline"
          >
            🗑️ Delete
          </button>
        </li>
      ))}
    </ul>
    {/* Pagination Controls */}
    {totalPages > 1 && (
      <div className="flex justify-center gap-2 mt-2">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
          <button
            key={num}
            className={`px-2 py-1 rounded text-xs font-semibold ${num === currentPage ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            onClick={() => setCurrentPage(num)}
          >
            {num}
          </button>
        ))}
      </div>
    )}
  </div>
)}
<div className="text-center mb-4 flex flex-wrap justify-center gap-2">
  {role === 'student' && (
    <button
      onClick={() => setShowJournal(!showJournal)}
      className={`text-sm px-4 py-1 rounded-full shadow mx-2 ${showJournal ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}
    >
      {showJournal ? '🙈 Hide Journal' : '📘 View My Journal'}
    </button>
  )}
</div>

{/* Book upload */}
 <div className="bg-pink-50 border border-pink-200 rounded-xl p-4 mb-4">
    <h2 className="text-lg font-bold text-pink-800 mb-2">📚 Upload a Book</h2>
    <input
      id="bookUpload"
      type="file"
      accept="application/pdf"
      ref={fileInputRef}
      className="mb-2"
    />
   {accessType === "pro" ? (
  // PRO USER: Show real upload button
  <button
    onClick={handleUpload}
    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold"
  >
    📥 Upload a Book
  </button>
) : (
  // DEMO USER: Show disabled/redirecting button
<button
  onClick={() => setShowUpgradeModal(true)}
  className="bg-gray-300 text-gray-700 px-4 py-2 rounded font-bold cursor-pointer hover:bg-yellow-300 hover:text-yellow-900 transition"
>
  🔒 Upload a Book (Pro only)
</button>

)}

  </div>


  <button
    onClick={() => setShowBooks(prev => !prev)}
    className={`mb-2 px-3 py-1 rounded font-bold ${showBooks ? "bg-yellow-200 text-yellow-800" : "bg-gray-200 text-gray-700"}`}
  >
    {showBooks ? "🙈 Hide Uploaded Books" : "📚 Show Uploaded Books"}
  </button>

  {showBooks && (
    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl mb-6">
      <h2 className="text-lg font-bold text-yellow-800 mb-3">📚 Uploaded Books</h2>
      {/* ...your Delete All button and book list here... */}
            <button
    onClick={handleDeleteAllBooks}
    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded mb-4 font-bold"
  >
    🗑️ Delete All Books
  </button>
  {role === 'student' && accessType === 'pro' && (
  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl mb-6">
    <h2 className="text-lg font-bold text-yellow-800 mb-3">📚 Uploaded Books</h2>

    {loadingBooks ? (
      <p className="text-sm text-gray-600">Loading books...</p>
    ) : books.length === 0 ? (
      <p className="text-sm text-gray-500">No books uploaded yet.</p>
    ) : (

      
      <ul className="space-y-3">
        {books.map(book => (          
          <li key={book.id} className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
            <div className="font-semibold text-gray-800">{book.name || 'Untitled Book'}</div>
            <div className="flex flex-wrap gap-2 mt-2">
              <button
  onClick={() => handleReadBook(book)}
  className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs hover:bg-purple-200"
>
  🤖 Brief the Book
</button>
  <button
    onClick={() => handleReadBook(book)}
    className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs hover:bg-purple-200"
  >
    🤖 Summarize Book
  </button>
  <button
    onClick={() => handleReadBookWithQuestion(book)}
    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs hover:bg-blue-200"
  >
    ❓ Ask About Book
  </button>
  <button
    onClick={() => handleReadBookQuiz(book)}
    className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs hover:bg-green-200"
  >
    📝 Quiz Me
  </button>
              <button className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs hover:bg-green-200">🎬 Movie Script</button>
              <button className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs hover:bg-indigo-200">📝 Model Exam</button>
              <button className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs hover:bg-purple-200">📊 Track Progress</button>
               <button
        onClick={() => handleDeleteBook(book)}
        className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs hover:bg-red-200"
      >
        🗑️ Delete
      </button> 
            </div>

          </li>
        ))}
      </ul>
    )}

  </div>
  
)}

    </div>
  )}

{questionModalBook && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white p-8 rounded-lg shadow-xl max-w-sm w-full relative">
      <h2 className="text-xl font-bold mb-4 text-blue-800">Ask Buddy about this book</h2>
      <div className="mb-4 text-gray-600">Book: <span className="font-semibold">{questionModalBook.name}</span></div>
      <input
        autoFocus
        type="text"
        className="w-full px-4 py-2 border border-blue-200 rounded focus:outline-none focus:ring focus:border-blue-400 mb-4"
        placeholder="Type your question here..."
        value={questionInput}
        onChange={e => setQuestionInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            if (questionInput.trim() !== "") {
              navigate(`/book-question/${encodeURIComponent(questionModalBook.name)}?q=${encodeURIComponent(questionInput)}`);
              setQuestionModalBook(null);
              setQuestionInput("");
            }
          }
        }}
      />
      <div className="flex gap-3">
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded font-bold hover:bg-blue-700"
          disabled={questionInput.trim() === ""}
          onClick={() => {
            if (questionInput.trim() !== "") {
              navigate(`/book-question/${encodeURIComponent(questionModalBook.name)}?q=${encodeURIComponent(questionInput)}`);
              setQuestionModalBook(null);
              setQuestionInput("");
            }
          }}
        >
          Ask Buddy
        </button>
        <button
          className="bg-gray-200 text-gray-600 px-4 py-2 rounded font-semibold hover:bg-gray-300"
          onClick={() => setQuestionModalBook(null)}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}




      <footer className="text-xs text-center text-gray-400 mt-4">
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">
          {t("PrivacyPolicy")}
        </a>
      </footer>
      {role === 'teacher' && renderTeacherStudents()}
    </div>
  );
};

export default BuddyBot;
